'use client';

import { useState, useCallback, useEffect } from 'react';

import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';

import { LoadingButton } from '@mui/lab';

import { GQLQuery } from '@/lib/client';
import { toastSuccess, toastError } from '@/lib/toast';
import { Q_SESSION_SELF } from '@/lib/queries/session.query';

import { Iconify } from '@/components/iconify';

const HELPDESK_URL = process.env.NEXT_PUBLIC_HELPDESK_URL || '';
const HELPDESK_API_KEY = process.env.NEXT_PUBLIC_HELPDESK_API_KEY || '';
const HELPDESK_API_SECRET = process.env.NEXT_PUBLIC_HELPDESK_API_SECRET || '';

const AUTH_HEADER = `token ${HELPDESK_API_KEY}:${HELPDESK_API_SECRET}`;

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const TICKET_TYPES = ['Bug', 'Feature Request', 'Question', 'Other'];

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  Open: 'info',
  Replied: 'success',
  Resolved: 'success',
  Closed: 'default',
};

interface Ticket {
  name: number;
  subject: string;
  status: string;
  priority: string;
  creation: string;
  ticket_type: string;
  agent_group: string;
}

interface Communication {
  name: string;
  sender: string;
  content: string;
  creation: string;
  communication_type: string;
}

// ---- API helpers ----

async function fetchJSON(url: string) {
  const res = await fetch(url, { headers: { Authorization: AUTH_HEADER } });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

async function fetchTickets(email: string): Promise<Ticket[]> {
  const filters = encodeURIComponent(JSON.stringify([['raised_by', '=', email]]));
  const fields = encodeURIComponent(
    JSON.stringify(['name', 'subject', 'status', 'priority', 'creation', 'ticket_type', 'agent_group'])
  );
  const { data } = await fetchJSON(
    `${HELPDESK_URL}/api/resource/HD%20Ticket?filters=${filters}&fields=${fields}&order_by=creation+desc&limit_page_length=20`
  );
  return data;
}

async function fetchThread(ticketId: number): Promise<Communication[]> {
  const filters = encodeURIComponent(
    JSON.stringify([
      ['reference_doctype', '=', 'HD Ticket'],
      ['reference_name', '=', String(ticketId)],
    ])
  );
  const fields = encodeURIComponent(
    JSON.stringify(['name', 'sender', 'content', 'creation', 'communication_type'])
  );
  const { data } = await fetchJSON(
    `${HELPDESK_URL}/api/resource/Communication?filters=${filters}&fields=${fields}&order_by=creation+asc&limit_page_length=50`
  );
  return data;
}

// ---- Component ----

export default function SupportWidget() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);

  // New ticket form
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [ticketType, setTicketType] = useState('Question');

  // Tickets list
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // Thread view
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [thread, setThread] = useState<Communication[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);

  const { data: session } = GQLQuery({
    query: Q_SESSION_SELF,
    queryAction: 'sessionSelf',
  });

  const userEmail = session?.user?.email || '';

  const loadTickets = useCallback(async () => {
    if (!userEmail) return;
    setTicketsLoading(true);
    try {
      const data = await fetchTickets(userEmail);
      setTickets(data);
    } catch {
      // silent
    } finally {
      setTicketsLoading(false);
    }
  }, [userEmail]);

  const openThread = useCallback(async (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setThreadLoading(true);
    try {
      const data = await fetchThread(ticket.name);
      setThread(data);
    } catch {
      setThread([]);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  // Load tickets when switching to history tab
  useEffect(() => {
    if (open && tab === 1) loadTickets();
  }, [open, tab, loadTickets]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (prev) {
        setSubject('');
        setDescription('');
        setPriority('Medium');
        setTicketType('Question');
        setSubmitted(false);
        setError('');
        setSelectedTicket(null);
        setThread([]);
        setTab(0);
      }
      return !prev;
    });
  }, []);

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${HELPDESK_URL}/api/resource/HD Ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: AUTH_HEADER,
        },
        body: JSON.stringify({
          subject: subject.trim(),
          description: description.trim(),
          priority,
          ticket_type: ticketType,
          raised_by: userEmail || 'support@hokela.co.ke',
          agent_group: 'Wallet',
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.exc || `Request failed (${res.status})`);
      }

      setSubmitted(true);
      toastSuccess('Support ticket submitted successfully!');
    } catch (err: any) {
      const msg = err?.message || 'Failed to submit ticket. Please try again.';
      setError(msg);
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!HELPDESK_URL || !HELPDESK_API_KEY) return null;

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return d;
    }
  };

  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();

  // ---- Render: New ticket form ----
  const renderNewTicket = () => {
    if (submitted) {
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.success.main, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify icon="mdi:check-circle" width={32} color="success.main" />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Ticket Submitted
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Your support ticket has been created. Our team will get back to you shortly.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setTab(1);
                setSubmitted(false);
              }}
            >
              View My Tickets
            </Button>
            <Button variant="text" size="small" onClick={toggle}>
              Close
            </Button>
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          Describe your issue and we&apos;ll create a support ticket for you.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {userEmail && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.info.main, 0.08),
            }}
          >
            <Typography variant="caption" color="info.main">
              Submitting as {userEmail}
            </Typography>
          </Box>
        )}

        <TextField
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          required
          size="small"
          placeholder="Brief summary of your issue"
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          required
          multiline
          rows={4}
          size="small"
          placeholder="Provide details about your issue..."
        />

        <Stack direction="row" spacing={1.5}>
          <TextField
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            select
            fullWidth
            size="small"
          >
            {PRIORITIES.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Category"
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value)}
            select
            fullWidth
            size="small"
          >
            {TICKET_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!subject.trim() || !description.trim()}
          startIcon={<Iconify icon="mdi:send" />}
          fullWidth
          sx={{ py: 1.5 }}
        >
          Submit Ticket
        </LoadingButton>
      </Stack>
    );
  };

  // ---- Render: Thread view ----
  const renderThread = () => {
    if (!selectedTicket) return null;

    return (
      <Stack spacing={2} sx={{ height: '100%' }}>
        {/* Back + ticket header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedTicket(null);
              setThread([]);
            }}
          >
            <Iconify icon="mdi:arrow-left" width={18} />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              #{selectedTicket.name} {selectedTicket.subject}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                label={selectedTicket.status}
                size="small"
                color={STATUS_COLOR[selectedTicket.status] || 'default'}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
              <Typography variant="caption" color="text.disabled">
                {formatDate(selectedTicket.creation)}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Divider />

        {/* Messages */}
        {threadLoading ? (
          <Stack spacing={1.5} sx={{ py: 2 }}>
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={60} />
            ))}
          </Stack>
        ) : thread.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No messages yet.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ flex: 1, overflow: 'auto' }}>
            {thread.map((msg) => {
              const isAgent = msg.sender !== userEmail;
              return (
                <Box
                  key={msg.name}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: isAgent
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.grey[500], 0.08),
                    borderLeft: isAgent
                      ? `3px solid ${theme.palette.primary.main}`
                      : `3px solid ${theme.palette.divider}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={600} color={isAgent ? 'primary.main' : 'text.secondary'}>
                      {isAgent ? 'Support' : 'You'}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      {formatDate(msg.creation)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {stripHtml(msg.content)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  };

  // ---- Render: Tickets list ----
  const renderTicketsList = () => {
    if (selectedTicket) return renderThread();

    if (ticketsLoading) {
      return (
        <Stack spacing={1.5} sx={{ py: 1 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} />
          ))}
        </Stack>
      );
    }

    if (tickets.length === 0) {
      return (
        <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
          <Iconify icon="mdi:ticket-outline" width={40} sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary">
            No tickets yet
          </Typography>
          <Button variant="outlined" size="small" onClick={() => setTab(0)}>
            Create one
          </Button>
        </Stack>
      );
    }

    return (
      <Stack spacing={1}>
        {tickets.map((ticket) => (
          <Box
            key={ticket.name}
            onClick={() => openThread(ticket)}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" noWrap sx={{ mb: 0.25 }}>
                  #{ticket.name} {ticket.subject}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    label={ticket.status}
                    size="small"
                    color={STATUS_COLOR[ticket.status] || 'default'}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                  <Chip
                    label={ticket.priority}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </Stack>
              </Box>
              <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: '0.65rem' }}>
                {formatDate(ticket.creation)}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  const openTicketCount = tickets.filter((t) => t.status === 'Open').length;

  return (
    <>
      {/* FAB Button */}
      <Fab
        onClick={toggle}
        aria-label="Support"
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          zIndex: 1050,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': { bgcolor: 'primary.dark' },
          '@media print': { display: 'none' },
        }}
      >
        <Iconify icon={open ? 'mdi:close' : 'mdi:chat'} width={24} />
      </Fab>

      {/* Popup */}
      {open && (
        <Card
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : 96,
            left: isMobile ? 0 : 24,
            zIndex: 1049,
            width: isMobile ? '100%' : 400,
            maxHeight: isMobile ? '100vh' : 'calc(100vh - 8rem)',
            borderRadius: isMobile ? 0 : 3,
            boxShadow: theme.shadows[20],
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            '@media print': { display: 'none' },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:help-circle-outline" width={22} />
              <Typography variant="subtitle1" fontWeight={600}>
                Help & Support
              </Typography>
            </Stack>
            <IconButton onClick={toggle} size="small" sx={{ color: 'inherit' }}>
              <Iconify icon="mdi:close" width={20} />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              setSelectedTicket(null);
              setThread([]);
            }}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              borderBottom: `1px solid ${theme.palette.divider}`,
              '& .MuiTab-root': { minHeight: 40, fontSize: '0.8rem', textTransform: 'none' },
            }}
          >
            <Tab label="New Ticket" icon={<Iconify icon="mdi:plus-circle-outline" width={16} />} iconPosition="start" />
            <Tab
              label="My Tickets"
              icon={
                <Badge badgeContent={openTicketCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}>
                  <Iconify icon="mdi:ticket-outline" width={16} />
                </Badge>
              }
              iconPosition="start"
            />
          </Tabs>

          {/* Content */}
          <CardContent sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            {tab === 0 ? renderNewTicket() : renderTicketsList()}
          </CardContent>
        </Card>
      )}
    </>
  );
}
